import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export const useThrottledData = <T>(data: T[], delay: number) => {
  const [throttledData, setThrottledData] = useState(data);
  const lastCall = useRef(0);
  const timeout = useRef<NodeJS.Timeout | undefined>(undefined);

  const updateData = useCallback(
    (newData: T[]) => {
      const now = Date.now();
      const timeSinceLastCall = now - lastCall.current;

      clearTimeout(timeout.current);

      if (timeSinceLastCall > delay) {
        setThrottledData(newData);
        lastCall.current = now;
      } else {
        const timeUntilNextCall = delay - timeSinceLastCall;
        timeout.current = setTimeout(() => {
          setThrottledData(newData);
          lastCall.current = Date.now();
        }, timeUntilNextCall);
      }
    },
    [delay],
  );

  // Layout effect to ensure the data is updated before the component is rendered
  // This is nessecary when the delay is 0 and we want to render the data immediately
  useLayoutEffect(() => {
    if (throttledData.length === 0 || delay === 0) {
      setThrottledData(data);
    } else {
      updateData(data);
    }

    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, [data, updateData, delay, throttledData.length]);

  return throttledData;
};
